import { useState, useEffect } from "react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { HttpMethod, callApi } from "src/services/apiService";
import Warning from "src/components/Warning";
import SaveInProgress from "src/components/SaveInProgress";
import { emailValidationRules } from "src/utils/emailValidation";
import { refreshAuthState } from "src/utils/authUtils";

type VerifyEmailFormFields = {
  email: string;
  verificationCode: string;
};

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authState = useAppSelector(selectAuth);
  const { userEmail, isEmailVerified } = authState;
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showWarning, setShowWarning] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState<string>("");
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [showChangeEmailForm, setShowChangeEmailForm] = useState(false);
  const [showEmailChoiceModal, setShowEmailChoiceModal] = useState(false);
  const [lastSavedEmail, setLastSavedEmail] = useState<string>(userEmail || "");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<VerifyEmailFormFields>({
    defaultValues: {
      email: userEmail || "",
      verificationCode: "",
    },
  });

  const watchedEmail = watch("email");

  // Check if email has been changed but not saved
  const hasUnsavedEmailChanges = watchedEmail !== lastSavedEmail;

  useEffect(() => {
    if (userEmail) {
      setValue("email", userEmail);
      setLastSavedEmail(userEmail);
    }
  }, [userEmail, setValue]);

  // Refresh auth data from server on component mount if needed
  useEffect(() => {
    const refreshAuthData = async () => {
      // If userEmail is undefined, try to refresh from server
      if (!userEmail) {
        console.log(
          "VerifyEmail - userEmail is undefined, refreshing from server...",
        );
        try {
          console.log(
            "VerifyEmail - userEmail is undefined, refreshing from server...",
          );
          await refreshAuthState(dispatch);
        } catch (error) {
          console.error("VerifyEmail - Failed to refresh auth data:", error);
        }
      }
    };

    refreshAuthData();
  }, [userEmail, dispatch]);

  useEffect(() => {
    console.log("VerifyEmail - Auth State:", {
      userEmail,
      isEmailVerified,
      lastSavedEmail,
      watchedEmail,
    });
  }, [userEmail, isEmailVerified, lastSavedEmail, watchedEmail]);

  const handleUpdateEmail = async (formData: VerifyEmailFormFields) => {
    setSaveInProgress(true);
    setShowWarning("");
    setShowSuccess("");

    try {
      const apiUrl = `mfa/changeemail?email=${encodeURIComponent(formData.email)}`;
      const response = await callApi(apiUrl, HttpMethod.POST);

      if (response.type === "SUCCESS") {
        setShowSuccess("Email address updated successfully!");
        setLastSavedEmail(formData.email);
        await refreshAuthState(dispatch);
      } else {
        setShowWarning(response.message || "Failed to update email address.");
      }
    } catch (error: any) {
      setShowWarning(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while updating email.",
      );
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleSendVerification = async () => {
    if (!watchedEmail) return;

    // If no email was previously saved, just update and send code
    if (!userEmail) {
      await handleUpdateEmail({ email: watchedEmail, verificationCode: "" });
      await sendVerificationCode(watchedEmail);
      return;
    }

    // If user has changed email but not saved it, show choice modal
    if (hasUnsavedEmailChanges && userEmail) {
      setShowEmailChoiceModal(true);
      return;
    }

    // Proceed with sending verification code
    await sendVerificationCode(watchedEmail);
  };

  const sendVerificationCode = async (emailToSend: string) => {
    setSaveInProgress(true);
    setShowWarning("");
    setShowSuccess("");

    try {
      const apiUrl = "mfa/SendEmailVerification";
      const response = await callApi(apiUrl, HttpMethod.POST, {
        email: emailToSend,
      });

      if (response.type === "SUCCESS") {
        setShowSuccess(
          `Verification code has been sent to ${emailToSend}.\nPlease check your email and enter the code sent to you.`,
        );
        setIsVerificationSent(true);
        setIsEmailLocked(true);
      } else {
        setShowWarning(
          response.message || "Failed to send verification email.",
        );
      }
    } catch (error: any) {
      setShowWarning(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while sending verification email.",
      );
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleUpdateAndSendCode = async () => {
    setShowEmailChoiceModal(false);
    // First update the email
    await handleUpdateEmail({ email: watchedEmail, verificationCode: "" });
    // Then send code to the new email
    await sendVerificationCode(watchedEmail);
  };

  const handleSendToOriginalEmail = async () => {
    setShowEmailChoiceModal(false);
    // Send code to the original email in database
    if (userEmail) {
      await sendVerificationCode(userEmail);
    }
  };

  const handleVerifyCode = async (formData: VerifyEmailFormFields) => {
    setSaveInProgress(true);
    setShowWarning("");
    setShowSuccess("");

    try {
      const apiUrl = `users/VerifyEmail?code=${encodeURIComponent(formData.verificationCode)}`;
      const response = await callApi(apiUrl, HttpMethod.POST);

      if (response.type === "SUCCESS") {
        setIsVerificationSent(false);
        setIsEmailLocked(false);
        setVerifyAttempts(0);
        setShowSuccess("");
        await refreshAuthState(dispatch);
        reset({ email: formData.email, verificationCode: "" });
      } else {
        const newAttempts = verifyAttempts + 1;
        setVerifyAttempts(newAttempts);

        if (newAttempts >= 3) {
          setShowWarning(
            "Maximum verification attempts reached. Please request a new code.",
          );

          setIsVerificationSent(false);
          setIsEmailLocked(false);
          setVerifyAttempts(0);
          setValue("verificationCode", "");
        } else {
          setShowWarning(
            `${response.message || "Invalid verification code. Please try again."} (${newAttempts}/3 attempts)`,
          );
        }
      }
    } catch (error: any) {
      const newAttempts = verifyAttempts + 1;
      setVerifyAttempts(newAttempts);

      if (newAttempts >= 3) {
        setShowWarning(
          "Maximum verification attempts reached. Please request a new code.",
        );
        setIsVerificationSent(false);
        setIsEmailLocked(false);
        setVerifyAttempts(0);
        setValue("verificationCode", "");
      } else {
        setShowWarning(
          `${error.response?.data?.message || error.message || "An error occurred while verifying the code."} (${newAttempts}/3 attempts)`,
        );
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  const resetVerificationFlow = () => {
    setIsVerificationSent(false);
    setIsEmailLocked(false);
    setShowWarning("");
    setShowSuccess("");
    setVerifyAttempts(0);
    setValue("verificationCode", "");
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">
                <i className="bi bi-envelope me-2"></i>
                Email Verification
              </h4>
            </div>
            <div className="card-body">
              {isEmailVerified && !showChangeEmailForm && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Your email address is verified!{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-underline"
                    onClick={() => navigate("/home")}
                  >
                    Click here to go home
                  </button>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowChangeEmailForm(true)}
                    >
                      Change Email Address?
                    </button>
                  </div>
                </div>
              )}

              {showWarning && <Warning message={showWarning} />}

              {showSuccess && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  {showSuccess}
                </div>
              )}

              {(!isEmailVerified || showChangeEmailForm) && (
                <form>
                  {/* Email Input Section */}
                  {!isVerificationSent && (
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          {...register("email", emailValidationRules)}
                          type="email"
                          className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          id="email"
                          placeholder="Enter your email address"
                          disabled={isEmailLocked}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleSubmit(handleUpdateEmail)}
                          disabled={
                            saveInProgress ||
                            isEmailLocked ||
                            !watchedEmail ||
                            watchedEmail === lastSavedEmail
                          }
                        >
                          {saveInProgress ? (
                            <SaveInProgress isVisible={true} />
                          ) : (
                            "Update"
                          )}
                        </button>
                      </div>
                      {errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email.message}
                        </div>
                      )}

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSendVerification}
                          disabled={
                            saveInProgress ||
                            !watchedEmail ||
                            isVerificationSent ||
                            (isEmailVerified && !hasUnsavedEmailChanges)
                          }
                        >
                          {saveInProgress ? (
                            <SaveInProgress isVisible={true} />
                          ) : (
                            "Send Code"
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {isVerificationSent && (
                    <div className="mb-3">
                      <label htmlFor="verificationCode" className="form-label">
                        Enter your code <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          {...register("verificationCode", {
                            required: "Verification code is required",
                          })}
                          type="text"
                          className={`form-control ${errors.verificationCode ? "is-invalid" : ""}`}
                          id="verificationCode"
                          placeholder="Enter verification code"
                          autoComplete="one-time-code"
                        />
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={handleSubmit(handleVerifyCode)}
                          disabled={saveInProgress}
                        >
                          {saveInProgress ? (
                            <SaveInProgress isVisible={true} />
                          ) : (
                            "Confirm"
                          )}
                        </button>
                      </div>
                      {errors.verificationCode && (
                        <div className="invalid-feedback d-block">
                          {errors.verificationCode.message}
                        </div>
                      )}

                      <div className="mt-2">
                        <small className="text-muted">
                          Didn't receive the code?{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-underline"
                            onClick={handleSendVerification}
                            disabled={saveInProgress}
                          >
                            Resend code
                          </button>
                        </small>
                      </div>

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-underline"
                          onClick={resetVerificationFlow}
                        >
                          Change email address
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Help Text */}
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>A verified email
                      address is required for full access to Help desk features.
                    </small>
                  </div>

                  {/* Debug Redux State */}
                  <div className="mt-4 p-3 bg-light border rounded d-none">
                    <h6 className="text-muted mb-2">🐛 Debug: Redux State</h6>
                    <div className="row text-sm">
                      <div className="col-6">
                        <strong>userEmail:</strong>{" "}
                        <code>{userEmail || "null"}</code>
                      </div>
                      <div className="col-6">
                        <strong>isEmailVerified:</strong>{" "}
                        <code>{isEmailVerified ? "true" : "false"}</code>
                      </div>
                      <div className="col-6">
                        <strong>lastSavedEmail:</strong>{" "}
                        <code>{lastSavedEmail || "null"}</code>
                      </div>
                      <div className="col-6">
                        <strong>watchedEmail:</strong>{" "}
                        <code>{watchedEmail || "null"}</code>
                      </div>
                      <div className="col-12 mt-2">
                        <strong>hasUnsavedEmailChanges:</strong>{" "}
                        <code>{hasUnsavedEmailChanges ? "true" : "false"}</code>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => refreshAuthState(dispatch)}
                      >
                        🔄 Refresh Redux State
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Choice Modal */}
      {showEmailChoiceModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Email Address Changed</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmailChoiceModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  You have changed your email address but haven't updated it
                  yet.
                </p>
                <p>
                  <strong>Original email:</strong> {userEmail}
                </p>
                <p>
                  <strong>New email:</strong> {watchedEmail}
                </p>
                <p>Would you like to:</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateAndSendCode}
                  disabled={saveInProgress}
                >
                  Update Email & Send Code
                </button>
                {!isEmailVerified && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleSendToOriginalEmail}
                    disabled={saveInProgress}
                  >
                    Send Code to Original Email
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowEmailChoiceModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyEmail;
